import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Coins } from 'lucide-react';

const tokenomicsData = [
  { name: 'Presale', value: 10, color: '#8B5CF6', description: 'Initial token sale for early supporters' },
  { name: 'Staking Rewards', value: 15, color: '#6366F1', description: 'Rewards for token stakers' },
  { name: 'Team & Partners', value: 15, color: '#14B8A6', description: 'Team allocation and strategic partnerships (locked)' },
  { name: 'Liquidity & CEX', value: 20, color: '#EC4899', description: 'Liquidity and centralized exchange reserves' },
  { name: 'Marketing', value: 10, color: '#F59E0B', description: 'Marketing and development fund' },
  { name: 'Airdrop', value: 3, color: '#10B981', description: 'Community airdrops and rewards' },
  { name: 'Burn', value: 10, color: '#EF4444', description: 'Token burn mechanism for deflationary pressure' },
  { name: 'Treasury / DAO', value: 17, color: '#8B5CF6', description: 'DAO treasury for governance and future development' }
];

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="quantum-card p-3 !bg-quantum-dark/95 backdrop-blur-sm border-quantum-primary/20">
        <p className="text-sm font-semibold text-quantum-primary mb-1">{data.name}</p>
        <p className="text-xs text-quantum-light mb-1">{data.value}%</p>
        <p className="text-xs text-quantum-light/80">{data.description}</p>
      </div>
    );
  }
  return null;
};

export default function Tokenomics() {
  return (
    <div className="py-8 w-full relative">
      <div className="absolute top-[-40px] hidden md:block left-[calc(50%-305px)] w-[300px] pointer-events-none z-0">
        <img 
          src="/gif/cyperjax.gif" 
          alt="Cyperjax" 
          className="w-full h-auto opacity-80" 
        />
      </div>
      
      <div className="absolute top-[-20px] md:hidden left-[calc(50%-160px)] w-[320px] pointer-events-none z-0 mobile-cyberjax-position">
        <img 
          src="/gif/cyperjax.gif" 
          alt="Cyperjax" 
          className="w-full h-auto opacity-80" 
        />
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h2 className="font-tokyo text-3xl font-bold bg-gradient-to-r from-quantum-primary to-quantum-secondary bg-clip-text text-transparent mb-2 relative z-20">
            Tokenomics
          </h2>
          <p className="text-sm text-quantum-light/80 relative z-0">Total Supply: 1,000,000,000 QGEN</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 relative z-10">
          <div className="quantum-card p-6 relative group">
            <h3 className="text-lg font-semibold text-quantum-light mb-4 flex items-center">
              <Coins className="w-5 h-5 mr-2" />
              Token Distribution
            </h3>
            <div className="h-[300px] relative">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={tokenomicsData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {tokenomicsData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.color}
                        className="transition-all duration-300 hover:opacity-80 cursor-pointer"
                        style={{ filter: 'drop-shadow(0 0 8px rgba(255,255,255,0.1))' }}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-4">
              {tokenomicsData.map((item) => (
                <div
                  key={item.name}
                  className="flex items-center p-2 rounded-lg hover:bg-white/5 transition-colors duration-200 cursor-pointer group"
                >
                  <div
                    className="w-3 h-3 rounded-full mr-2 group-hover:scale-110 transition-transform duration-200"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-xs text-quantum-light/70 group-hover:text-quantum-light transition-colors duration-200">
                    {item.name} ({item.value}%)
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <div className="quantum-card p-6">
              <h3 className="text-lg font-semibold text-quantum-light mb-4">Genesis Features</h3>
              <div className="space-y-4">
                <div className="quantum-card p-4 bg-gradient-to-r from-quantum-primary/5 to-quantum-secondary/5">
                  <p className="text-sm text-quantum-primary font-semibold mb-1">Quantum Boosts</p>
                  <p className="text-xs text-quantum-light/80">Weekly wallet boosts grant bonus staking rewards or surprise airdrops to active holders. Stay quantum-aligned for perks.</p>
                </div>
                <div className="quantum-card p-4 bg-gradient-to-r from-quantum-primary/5 to-quantum-secondary/5">
                  <p className="text-sm text-quantum-primary font-semibold mb-1">Meme Reactor</p>
                  <p className="text-xs text-quantum-light/80">Submit memes. Vote. Win. Quantum Genesis rewards the top meme every week—because memes move markets.</p>
                </div>
                <div className="quantum-card p-4 bg-gradient-to-r from-quantum-primary/5 to-quantum-secondary/5">
                  <p className="text-sm text-quantum-primary font-semibold mb-1">Genesis Quests</p>
                  <p className="text-xs text-quantum-light/80">Complete quests like linking socials, staking, or inviting friends to earn XP and unlock exclusive NFTs, roles, or airdrops.</p>
                </div>
                <div className="quantum-card p-4 bg-gradient-to-r from-quantum-primary/5 to-quantum-secondary/5">
                  <p className="text-sm text-quantum-primary font-semibold mb-1">Key Points</p>
                  <ul className="mt-2 space-y-2">
                    <li className="text-xs text-quantum-light/80"><span className="font-semibold text-quantum-light">Total initial circulation at TGE:</span> 15% (Presale + Airdrop + Initial Liquidity)</li>
                    <li className="text-xs text-quantum-light/80"><span className="font-semibold text-quantum-light">Community-focused allocations:</span> 45% (Staking + Treasury + Airdrop)</li>
                    <li className="text-xs text-quantum-light/80"><span className="font-semibold text-quantum-light">Strategic reserves:</span> 30% (Marketing + Liquidity/CEX)</li>
                    <li className="text-xs text-quantum-light/80"><span className="font-semibold text-quantum-light">Deflationary mechanism:</span> 10% (Burn)</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

